import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Card, Button, Select, Space, message, Spin, Tooltip } from 'antd';
import {
  EyeOutlined,
  SettingOutlined,
  BgColorsOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import SequenceViewer from './SequenceViewer';

const { Option } = Select;

interface Chain {
  id: string;
  name: string;
  sequence: Array<{
    id: number;
    name: string;
    code: string;
    chainId: string;
    position: number;
    secondaryStructure?: 'helix' | 'sheet' | 'loop';
  }>;
  color: string;
}

interface AdvancedMolStarViewerProps {
  structureData: string;
  structureFormat?: 'pdb' | 'sdf' | 'cif';
  height?: number | string;
  representation?: string;
  colorScheme?: string;
  showControls?: boolean;
  chains?: Chain[];
  onResidueSelect?: (residueIds: number[]) => void;
  onStructureHover?: (info: { chainId: string; position: number; name?: string } | null) => void;
  externalHoverLabel?: string;
}

export interface AdvancedMolStarViewerRef {
  getPlugin: () => any;
  setRepresentation: (repr: string) => void;
  setColorScheme: (scheme: string) => void;
  resetView: () => void;
  getChains: () => Chain[];
  highlightResidues: (residueIds: number[], chainId?: string, options?: { showLabel?: boolean; labelText?: string }) => void;
  clearHighlight: () => void;
  screenshot: () => Promise<string | null>;
}

const AdvancedMolStarViewer = forwardRef<AdvancedMolStarViewerRef, AdvancedMolStarViewerProps>(({
  structureData,
  structureFormat = 'pdb',
  height = 600,
  representation: propRepresentation = 'cartoon',
  colorScheme: propColorScheme = 'chain-id',
  showControls = true,
  chains = [],
  onResidueSelect,
  onStructureHover,
  externalHoverLabel
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pluginRef = useRef<any>(null);
  const [plugin, setPlugin] = useState<any>(null);
  const [representation, setRepresentation] = useState<string>(propRepresentation);
  const [colorScheme, setColorScheme] = useState<string>(propColorScheme);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extractedChains, setExtractedChains] = useState<Chain[]>([]);
  const [highlightedResidues, setHighlightedResidues] = useState<number[]>([]);
  const isUnmountingRef = useRef(false);
  const initializedRef = useRef(false);
  const lastRepRef = useRef<string | null>(null);
  const [hoverLabel, setHoverLabel] = useState<string>('');

  // 暴露给父组件的方法
  useImperativeHandle(ref, () => ({
    getPlugin: () => pluginRef.current,
    setRepresentation: (repr: string) => {
      setRepresentation(repr);
    },
    setColorScheme: (scheme: string) => {
      setColorScheme(scheme);
    },
    resetView: async () => {
      if (plugin?.instance) {
        try {
          await plugin.instance.managers.camera.reset();
        } catch (error) {
          console.warn('Reset view failed:', error);
        }
      }
    },
    getChains: () => extractedChains,
    highlightResidues: async (residueIds: number[], chainId?: string, options?: { showLabel?: boolean; labelText?: string }) => {
      if (!plugin?.instance) return;
      
      try {
        // 清除之前的高亮（包括 Mol* 内置高亮与我们创建的组件/表示）
        await clearHighlightInternal();
        setHighlightedResidues(residueIds);
        
        // 使用 Mol* 表达式只选中指定链与残基
        const structure = plugin.structure;
        if (structure && residueIds.length > 0) {
          try {
            const MS: any = (await import('molstar/lib/mol-script/language/builder')).MolScriptBuilder as any;
            const atomProps: any = MS.struct.atomProperty as any;
            const mm: any = atomProps.macromolecular ?? atomProps.macromolecule;
            if (!mm) throw new Error('Mol* macromolecule atomProperty group not available');
            const seqLabel = mm.label_seq_id();
            const chainLabel = mm.label_asym_id();
            const seqAuth = typeof mm.auth_seq_id === 'function' ? mm.auth_seq_id() : null;
            const chainAuth = typeof mm.auth_asym_id === 'function' ? mm.auth_asym_id() : null;

            // 组装 residue 测试（同时支持 auth 与 label）
            const residueTest = (() => {
              if (residueIds.length === 1) {
                const labelEq = MS.core.rel.eq([ seqLabel, residueIds[0] ]);
                if (seqAuth) {
                  const authEq = MS.core.rel.eq([ seqAuth, residueIds[0] ]);
                  return MS.core.logic.or([authEq, labelEq]);
                }
                return labelEq;
              } else {
                const setVals = MS.core.type.set(residueIds);
                const hasLabel = MS.core.set.has([ setVals, seqLabel ]);
                if (seqAuth) {
                  const hasAuth = MS.core.set.has([ setVals, seqAuth ]);
                  return MS.core.logic.or([hasAuth, hasLabel]);
                }
                return hasLabel;
              }
            })();

            const makeExpr = (withChain: boolean, mode: 'both'|'label'|'auth' = 'both') => {
              const p: any = { };
              // 根据模式构建 residue-test
              if (mode === 'label') {
                if (residueIds.length === 1) {
                  p['residue-test'] = MS.core.rel.eq([ seqLabel, residueIds[0] ]);
                } else {
                  const setVals = MS.core.type.set(residueIds);
                  p['residue-test'] = MS.core.set.has([ setVals, seqLabel ]);
                }
              } else if (mode === 'auth' && seqAuth) {
                if (residueIds.length === 1) {
                  p['residue-test'] = MS.core.rel.eq([ seqAuth, residueIds[0] ]);
                } else {
                  const setVals = MS.core.type.set(residueIds);
                  p['residue-test'] = MS.core.set.has([ setVals, seqAuth ]);
                }
              } else {
                p['residue-test'] = residueTest;
              }
              if (withChain && chainId) {
                const labelEq = MS.core.rel.eq([ chainLabel, chainId ]);
                p['chain-test'] = chainAuth
                  ? MS.core.logic.or([ MS.core.rel.eq([ chainAuth, chainId ]), labelEq ])
                  : labelEq;
              }
              return MS.struct.generator.atomGroups(p);
            };

            // 尝试：带链 + 双模 (auth/label)
            let expr = makeExpr(true, 'both');
            let comp = await plugin.instance.builders.structure.tryCreateComponentFromExpression(structure, expr, 'highlight');
            
            // 回退1：不带链 + 双模
            if (!comp) {
              expr = makeExpr(false, 'both');
              comp = await plugin.instance.builders.structure.tryCreateComponentFromExpression(structure, expr, 'highlight');
            }

            // 回退2：带链 + label-only
            if (!comp) {
              expr = makeExpr(true, 'label');
              comp = await plugin.instance.builders.structure.tryCreateComponentFromExpression(structure, expr, 'highlight');
            }

            // 回退3：带链 + auth-only
            if (!comp) {
              expr = makeExpr(true, 'auth');
              comp = await plugin.instance.builders.structure.tryCreateComponentFromExpression(structure, expr, 'highlight');
            }

            if (!comp) throw new Error('Empty selection for highlight');

            // 使用 Mol* 内置的高亮系统（不调用可能触发 label 的方法）
            try {
              const stateSel = plugin.instance.state.data.select(comp.ref)[0];
              const { StructureElement } = await import('molstar/lib/mol-model/structure');
              if (stateSel?.obj?.data) {
                const loci = StructureElement.Loci.all(stateSel.obj.data);
                // 确保 loci 对象有效且有 kind 属性后才调用高亮
                if (loci && typeof loci === 'object' && loci.kind && StructureElement.Loci.is(loci)) {
                  const lh: any = (plugin.instance as any)?.managers?.interactivity?.lociHighlights;
                  // 使用 addHighlight 而不是 highlightOnly，避免触发 label 系统
                  if (lh?.addHighlight) {
                    lh.addHighlight({ loci, repr: undefined });
                  } else if (lh?.highlight) {
                    lh.highlight(loci);
                  }
                }
              }
            } catch (highlightError) {
              console.warn('Failed to apply loci highlight:', highlightError);
            }

            // 标注右下角信息（可选）
            if (options?.showLabel && options?.labelText) setHoverLabel(options.labelText);

            // 保存组件引用以便后续清除（避免状态膨胀）
            plugin.highlightComponentRef = comp?.ref;
            plugin.highlightReprRef = null;

            await plugin.instance.canvas3d?.requestDraw();

            console.log(`Highlighted residues (loci highlight only): ${residueIds.join(', ')} in chain ${chainId || 'all'}`);
          } catch (reprError) {
            console.warn('Failed to create chain-specific highlight:', reprError);
          }
        }
      } catch (error) {
        console.warn('Failed to highlight residues:', error);
      }
    },
    clearHighlight: () => clearHighlightInternal(),
    screenshot: async () => {
      try {
        const inst: any = plugin?.instance;
        const canvasEl = containerRef.current?.querySelector('canvas') as HTMLCanvasElement | null;
        if (!inst || !canvasEl) return null;

        const displayW = canvasEl.clientWidth || canvasEl.width || 800;
        const displayH = canvasEl.clientHeight || canvasEl.height || 600;
        const scale = 4; // 最高分辨率（4x）
        const targetW = Math.max(1, Math.floor(displayW * scale));
        const targetH = Math.max(1, Math.floor(displayH * scale));

        // 优先使用 Mol* 的高分辨率截图接口（如果可用）
        const c3d: any = inst.canvas3d;
        if (c3d?.getImageData) {
          try {
            const result = await c3d.getImageData(targetW, targetH, true);
            // 可能返回 ImageData 或包含 data/width/height 的对象，或 dataURL 字符串
            if (result) {
              if (typeof result === 'string') return result;
              if ((result as any).data && (result as any).width && (result as any).height) {
                const off = document.createElement('canvas');
                off.width = (result as any).width;
                off.height = (result as any).height;
                const ctx = off.getContext('2d');
                if (ctx && (result as any).data) {
                  const imageData = new ImageData(new Uint8ClampedArray((result as any).data), (result as any).width, (result as any).height);
                  ctx.putImageData(imageData, 0, 0);
                  return off.toDataURL('image/png');
                }
              }
            }
          } catch (e) {
            console.warn('Mol* getImageData 失败，改用回退方案:', e);
          }
        }

        // 回退：请求一次重绘后，将当前 WebGL 画面拷贝到更大离屏画布
        await c3d?.requestDraw?.();
        await new Promise(r => setTimeout(r, 50));
        const off = document.createElement('canvas');
        off.width = targetW;
        off.height = targetH;
        const ctx = off.getContext('2d');
        if (ctx) {
          ctx.drawImage(canvasEl, 0, 0, targetW, targetH);
          return off.toDataURL('image/png');
        }
        return canvasEl.toDataURL('image/png');
      } catch (e) {
        console.warn('截图失败:', e);
        return null;
      }
    }
  }), [plugin, extractedChains]);

  // 清除高亮的内部方法
  const clearHighlightInternal = async () => {
    if (!plugin?.instance) return;
    
    try {
      setHighlightedResidues([]);
      setHoverLabel('');
      
      // 清除 Mol* 内置高亮
      try {
        const lh: any = (plugin.instance as any)?.managers?.interactivity?.lociHighlights;
        lh?.clearHighlights?.();
      } catch {}

      // 清除之前的高亮表示
      if (plugin.highlightRepr) {
        try {
          await plugin.instance.state.data.remove(plugin.highlightRepr.ref);
          plugin.highlightRepr = null;
        } catch (removeError) {
          console.warn('Failed to remove highlight representation:', removeError);
        }
      }

      // 新的引用字段：组件与表示
      if (plugin.highlightReprRef) {
        try { await plugin.instance.state.data.remove(plugin.highlightReprRef); } catch {}
        plugin.highlightReprRef = null;
      }
      if (plugin.highlightComponentRef) {
        try { await plugin.instance.state.data.remove(plugin.highlightComponentRef); } catch {}
        plugin.highlightComponentRef = null;
      }
    } catch (error) {
      console.warn('Failed to clear highlight:', error);
    }
  };

  // 将外部格式映射为 Mol* 内部格式键
  type MolstarFormat = 'pdb' | 'sdf' | 'mmcif';
  const getMolstarFormat = (fmt?: 'pdb' | 'sdf' | 'cif'): MolstarFormat => {
    if (fmt === 'sdf') return 'sdf';
    if (fmt === 'cif') return 'mmcif';
    return 'pdb';
  };

  // 初始化MolStar viewer
  useEffect(() => {
    if (!containerRef.current || !structureData) return;
    if (initializedRef.current) return; // 避免重复初始化

    const initMolStar = async () => {
      try {
        isUnmountingRef.current = false;
        setLoading(true);
        setError(null);

        console.log('开始初始化 MolStar...');

        // 动态导入Mol*非UI插件
        const { PluginContext } = await import('molstar/lib/mol-plugin/context');
        const { DefaultPluginSpec } = await import('molstar/lib/mol-plugin/spec');
        const { PluginConfig } = await import('molstar/lib/mol-plugin/config');

        // 创建插件配置（保留默认交互，启用 hover 高亮）
        const spec = DefaultPluginSpec();
        spec.config = [
          [PluginConfig.General.DisableAntialiasing, false],
          [PluginConfig.General.PixelScale, 1],
          [PluginConfig.Viewport.ShowAnimation, false]
        ];
        // 不再清空 behaviors，避免禁用 Mol* 内置 hover 高亮

        // 创建插件实例（无UI）
        const molstarPlugin = new PluginContext(spec);
        molstarPlugin.mount(containerRef.current!);
        await molstarPlugin.init();

        // 调整交互参数：禁用自动聚焦，确保高亮渲染开启
        try {
          molstarPlugin.canvas3d?.setProps({
            interaction: { autoFocus: false as any, focusTimeout: 0 as any },
            marking: { enabled: true as any }
          } as any);
        } catch {}
        
        console.log('MolStar 插件创建成功，开始加载结构数据...');
        
        // 加载PDB数据
        const data = await molstarPlugin.builders.data.rawData({ 
          data: structureData, 
          label: 'Structure Analysis' 
        });
        
        const trajectory = await molstarPlugin.builders.structure.parseTrajectory(data, getMolstarFormat(structureFormat));
        const model = await molstarPlugin.builders.structure.createModel(trajectory);
        const structure = await molstarPlugin.builders.structure.createStructure(model);
        
        // 添加默认表示
        const reprParams = getRepresentationParams(representation, colorScheme);
        const initialRep = await molstarPlugin.builders.structure.representation.addRepresentation(structure, reprParams);
        try {
          lastRepRef.current = (initialRep as any)?.transform?.ref ?? null;
        } catch {}
        
        // 强制渲染
        await molstarPlugin.canvas3d?.requestDraw();
        
        // 聚焦到结构
        try {
          const structureRef = molstarPlugin.state.data.select(structure.ref)[0];
          if (structureRef?.obj?.data) {
            const { StructureElement } = await import('molstar/lib/mol-model/structure');
            const wholeLoci = StructureElement.Loci.all(structureRef.obj.data);
            await molstarPlugin.managers.camera.focusLoci([wholeLoci]);
          } else {
            await molstarPlugin.managers.camera.reset();
          }
        } catch (focusError) {
          console.warn('聚焦结构失败:', focusError);
          await molstarPlugin.managers.camera.reset();
        }
        
        pluginRef.current = molstarPlugin;
        setPlugin({ instance: molstarPlugin, structure });
        initializedRef.current = true;
        setLoading(false);
        
        console.log('MolStar 查看器初始化成功');
        message.success('结构加载成功');
        
      } catch (error) {
        console.error('MolStar 初始化失败:', error);
        setError(`MolStar 初始化失败: ${error}`);
        setLoading(false);
      }
    };

    initMolStar();

    // 清理函数
    return () => {
      isUnmountingRef.current = true;
      initializedRef.current = false;
      try {
        pluginRef.current?.dispose?.();
      } catch (e) {
        console.warn('释放 MolStar 插件时出错:', e);
      } finally {
        pluginRef.current = null;
      }
      setPlugin(null);
    };
  }, [structureData, structureFormat]);

  // 获取表示参数
  const getRepresentationParams = (repr: string, color: string) => {
    const colorThemeName = getColorParams(color);
    console.log(`getRepresentationParams - repr: "${repr}", color: "${color}", colorTheme: "${colorThemeName}"`);

    switch (repr) {
      case 'cartoon':
        return {
          type: 'cartoon',
          colorTheme: { name: colorThemeName }
        } as const;
      case 'backbone':
        return {
          type: 'backbone',
          colorTheme: { name: colorThemeName }
        } as const;
      case 'ball+stick':
        return {
          type: 'ball-and-stick',
          colorTheme: { name: colorThemeName }
        } as const;
      case 'spacefill':
        return {
          type: 'spacefill',
          colorTheme: { name: colorThemeName }
        } as const;
      case 'surface':
        return {
          type: 'molecular-surface',
          colorTheme: { name: colorThemeName }
        } as const;
      default:
        console.warn(`Unknown representation type: "${repr}", falling back to cartoon`);
        return {
          type: 'cartoon',
          colorTheme: { name: colorThemeName }
        } as const;
    }
  };

  // 获取颜色参数 - 使用 MolStar 内置的颜色主题名称
  const getColorParams = (scheme: string) => {
    switch (scheme) {
      case 'chain-id':
        return 'chain-id' as const;
      case 'residue-name':
        return 'residue-name' as const;
      case 'secondary-structure':
        return 'secondary-structure' as const;
      case 'b-factor':
        return 'uncertainty' as const;
      case 'element-symbol':
        return 'element-symbol' as const;
      default:
        return 'chain-id' as const;
    }
  };

  // 更新表示的方法
  const updateRepresentation = async (molstarPlugin: any, structure: any, repr: string, color: string) => {
    try {
      console.log(`开始更新表示: ${repr}, 颜色: ${color}`);
      
      // 获取表示参数
      const reprParams = getRepresentationParams(repr, color);
      
      // 简单粗暴的方法：直接删除所有表示，然后重新创建
      try {
        // 清除所有数据
        await molstarPlugin.clear();
        
        // 重新加载PDB数据
        const data = await molstarPlugin.builders.data.rawData({ 
          data: structureData, 
          label: 'Structure Analysis' 
        });
        
        const trajectory = await molstarPlugin.builders.structure.parseTrajectory(data, getMolstarFormat(structureFormat));
        const model = await molstarPlugin.builders.structure.createModel(trajectory);
        const newStructure = await molstarPlugin.builders.structure.createStructure(model);
        
        // 添加新表示
        const newRepresentation = await molstarPlugin.builders.structure.representation.addRepresentation(newStructure, reprParams);
        
        // 更新plugin状态
        setPlugin({ instance: molstarPlugin, structure: newStructure });
        
        
        try {
          lastRepRef.current = (newRepresentation as any)?.transform?.ref ?? null;
        } catch {}
        
        // 聚焦到结构
        try {
          const structureRef = molstarPlugin.state.data.select(newStructure.ref)[0];
          if (structureRef?.obj?.data) {
            const { StructureElement } = await import('molstar/lib/mol-model/structure');
            const wholeLoci = StructureElement.Loci.all(structureRef.obj.data);
            await molstarPlugin.managers.camera.focusLoci([wholeLoci]);
          }
        } catch {}
        
      } catch (clearError) {
        console.warn('清除重建失败，尝试直接添加表示:', clearError);
        // 如果清除失败，直接添加新表示
        const newRepresentation = await molstarPlugin.builders.structure.representation.addRepresentation(structure, reprParams);
        try {
          lastRepRef.current = (newRepresentation as any)?.transform?.ref ?? null;
        } catch {}
      }
      
      // 强制重绘
      await molstarPlugin.canvas3d?.requestDraw();
      
      console.log(`表示更新完成: ${repr}, 颜色: ${color}`);
    } catch (error) {
      console.error('更新表示失败:', error);
      throw error;
    }
  };

  // 更新表示
  useEffect(() => {
    if (plugin?.instance && plugin?.structure && !loading && !isUnmountingRef.current) {
      updateRepresentation(plugin.instance, plugin.structure, representation, colorScheme)
        .catch(error => {
          if (!isUnmountingRef.current) {
            console.error('更新表示失败:', error);
            message.error('更新显示样式失败');
          }
        });
    }
  }, [representation, colorScheme]);

  // 监听外部传入的representation和colorScheme变化
  useEffect(() => {
    setRepresentation(propRepresentation);
  }, [propRepresentation]);

  useEffect(() => {
    setColorScheme(propColorScheme);
  }, [propColorScheme]);

  // 注释掉手动的 mousemove 监听器，使用下面的 MolStar hover 行为订阅（更稳定）
  // useEffect(() => {
  //   ... 代码已移除，改用 MolStar 内置 hover 行为订阅 ...
  // }, [plugin, onStructureHover]);

  // 使用 Mol* 内置的 hover 行为订阅（优先使用，比手动 identify 更稳定）
  useEffect(() => {
    if (!plugin?.instance) return;

    let unsub: any = null;
    try {
      const behavior = (plugin.instance as any)?.behaviors?.interaction?.hover;
      if (!behavior || !behavior.subscribe) return;

      unsub = behavior.subscribe(async (event: any) => {
        try {
          const loci = event?.current?.loci;
          // 增加更严格的检查：确保 loci 存在且有 kind 属性
          if (!loci || typeof loci !== 'object' || !loci.kind) {
            setHoverLabel('');
            onStructureHover?.(null);
            return;
          }

          const { StructureElement, StructureProperties } = await import('molstar/lib/mol-model/structure');
          
          // 检查 loci 是否为 StructureElement.Loci 类型
          if (!StructureElement.Loci.is(loci)) {
            setHoverLabel('');
            onStructureHover?.(null);
            return;
          }
          
          const first = StructureElement.Loci.getFirstLocation(loci as any);
          if (!first) {
            setHoverLabel('');
            onStructureHover?.(null);
            return;
          }

          const authAsym = StructureProperties.chain.auth_asym_id(first);
          const labelAsym = StructureProperties.chain.label_asym_id(first);
          const chainId = authAsym || labelAsym;

          const authSeq = StructureProperties.residue.auth_seq_id(first);
          const labelSeq = StructureProperties.residue.label_seq_id(first);
          const position = authSeq || labelSeq;

          let resName = '';
          try {
            resName = (StructureProperties as any)?.atom?.label_comp_id?.(first)
              ?? (StructureProperties as any)?.residue?.label_comp_id?.(first)
              ?? (StructureProperties as any)?.residue?.auth_comp_id?.(first)
              ?? '';
          } catch {
            resName = '';
          }

          if (chainId && position) {
            const text = `${resName || 'UNK'} ${position} | Chain ${chainId}`;
            setHoverLabel(text);
            onStructureHover?.({ chainId, position, name: resName || 'UNK' });
          } else {
            setHoverLabel('');
            onStructureHover?.(null);
          }
        } catch (err) {
          // 忽略 hover 解析错误，但在开发环境打印日志
          if (process.env.NODE_ENV === 'development') {
            console.warn('Hover event processing error:', err);
          }
        }
      });
    } catch (err) {
      // 行为订阅不可用时忽略，但在开发环境打印日志
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to subscribe to hover behavior:', err);
      }
    }

    return () => {
      try { unsub?.unsubscribe?.(); } catch {}
    };
  }, [plugin, onStructureHover]);


  return (
    <div style={{ position: 'relative', height }}>
      <div 
        ref={containerRef}
        style={{ 
          width: '100%', 
          height: typeof height === 'string' ? height : `${height}px`,
          borderRadius: '4px',
          overflow: 'hidden'
        }} 
      />
      

      {/* 加载状态 */}
      {loading && (
        <div style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 1000
        }}>
          <Space direction="vertical" align="center">
            <Spin size="large" />
            <div style={{ color: '#666' }}>正在加载MolStar查看器...</div>
          </Space>
        </div>
      )}
      
      {/* 残基信息右下角浮层 - 仅在没有外部标签时显示（避免重复） */}
      {!externalHoverLabel && hoverLabel && (
        <div style={{
          position: 'absolute',
          right: 16,
          bottom: 16,
          background: 'linear-gradient(135deg, rgba(255, 77, 79, 0.95) 0%, rgba(220, 20, 60, 0.95) 100%)',
          color: '#fff',
          borderRadius: 8,
          padding: '10px 14px',
          fontSize: 13,
          fontFamily: 'monospace',
          fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
          backdropFilter: 'blur(4px)',
          letterSpacing: '0.5px',
          zIndex: 1000,
          pointerEvents: 'none',
          minWidth: '180px',
          textAlign: 'center'
        }}>
          {hoverLabel}
        </div>
      )}
      
      
      <div style={{ marginTop: 8, fontSize: '12px', color: '#666', textAlign: 'center' }}>
        提示: 使用鼠标拖拽旋转，滚轮缩放，右键拖拽平移
        {highlightedResidues.length > 0 && (
          <span style={{ color: '#ff6b35', marginLeft: '8px' }}>
            已高亮 {highlightedResidues.length} 个残基
          </span>
        )}
      </div>
    </div>
  );
});

AdvancedMolStarViewer.displayName = 'AdvancedMolStarViewer';

export default AdvancedMolStarViewer;
